<template>
  <div class="fill-height">
    <v-card flat>
      <v-card-title class="py-0 px-1">
        <toolbar-period-selector
          v-model="dataTable.filter.filter_date"
          :default-period="null"
          :mandatory="false"
          hide-year
        >
          <v-btn
            v-if="userCanManage && (userIsOwner || userIsSuperAdmin)"
            :color="$t('Payment.Table.Toolbar.Button.Export.Color')"
            :disabled="!dataTable.filter.filter_date"
            :icon="$vuetify.breakpoint.xsOnly"
            :small="$vuetify.breakpoint.xsOnly"
            class="elevation-0 ma-1"
            text
            @click="onExportButtonClick"
          >
            <v-icon :left="$vuetify.breakpoint.smAndUp">
              {{ $t('Payment.Table.Toolbar.Button.Export.Icon') }}
            </v-icon>&nbsp;
            <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Payment.Table.Toolbar.Button.Export.Title').toLocaleUpperCase($i18n.locale) }}</span>
          </v-btn>

          <v-btn
            v-if="posUserHasComponentPermission('Receipts', 'ACCESS')"
            :color="$t('Payment.Table.Toolbar.Button.Receipts.Color')"
            :icon="$vuetify.breakpoint.xsOnly"
            :small="$vuetify.breakpoint.xsOnly"
            :to="{name: 'Receipts'}"
            class="elevation-0 ma-1"
            text
          >
            <v-icon
              v-if="$t('Payment.Table.Toolbar.Button.Receipts.Icon')"
              :left="$vuetify.breakpoint.smAndUp"
            >
              {{ $t('Payment.Table.Toolbar.Button.Receipts.Icon') }}
            </v-icon>&nbsp;

            <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Payment.Table.Toolbar.Button.Receipts.Title').toLocaleUpperCase($i18n.locale) }}</span>
          </v-btn>

          <v-btn
            v-if="appHasEpayTerminals"
            :color="$t('Payment.Table.Toolbar.Button.ResendAll.Color')"
            class="elevation-0 ma-1 px-1"
            depressed
            small
            @click="showTerminalResendAllDialog(TerminalVendorEnum.EPAY)"
          >
            <v-avatar
              class="rounded pa-1 mr-1"
              color="grey lighten-3"
              height="auto"
              tile
              width="60"
            >
              <v-img
                :src="`/assets/img/payment/logo-epay-dark.png`"
                contain
                height="auto"
                width="60"
              />
            </v-avatar>

            <span>{{ $t('Payment.Table.Toolbar.Button.ResendAll.Title').toLocaleUpperCase($i18n.locale) }}</span>
          </v-btn>

          <v-btn
            v-if="appHasEdpsTerminals"
            :color="$t('Payment.Table.Toolbar.Button.ResendAll.Color')"
            class="elevation-0 ma-1 px-1"
            depressed
            small
            @click="showTerminalResendAllDialog(TerminalVendorEnum.EDPS)"
          >
            <v-avatar
              class="rounded pa-1 mr-1"
              color="grey lighten-3"
              height="auto"
              tile
              width="60"
            >
              <v-img
                :src="`/assets/img/payment/logo-edps-dark.png`"
                contain
                height="auto"
                width="60"
              />
            </v-avatar>

            <span>{{ $t('Payment.Table.Toolbar.Button.ResendAll.Title').toLocaleUpperCase($i18n.locale) }}</span>
          </v-btn>

          <v-spacer />

          <filter-search v-model="dataTable.search.terms" />

          <filter-options
            v-model="filtersModel"
            :filters="filterData"
            max-width="450"
          />
        </toolbar-period-selector>
      </v-card-title>

      <v-container
        class="pa-0"
        fluid
      >
        <v-row>
          <v-col cols="12">
            <v-data-table
              :footer-props="dataTable.footerProps"
              :headers="dataTableHeaders"
              :height="dataTable.resize.bodyHeight"
              :items="dataTable.data"
              :loading="dataTable.loading"
              :loading-text="dataTable.text.loadingText"
              :no-data-text="dataTable.text.noDataText"
              :no-results-text="dataTable.text.noResultsText"
              :options.sync="dataTable.options"
              :search="dataTable.search.terms"
              :server-items-length="dataTable.total"
              fixed-header
              item-key="Id"
            >
              <template #[`item.id`]="{item}">
                {{ item.Id }}
              </template>

              <template #[`item.transaction_id`]="{item}">
                <v-btn
                  class="pa-1"
                  small
                  text
                  @click="getItemDetails(item)"
                >
                  {{ item.TransactionId }}
                </v-btn>
              </template>

              <template #[`item.order_code`]="{item}">
                {{ item.OrderCode }}
              </template>

              <template #[`item.terminal`]="{item}">
                <div style="line-height: 14px;">
                  <div
                    v-if="!!item.TerminalNickname"
                    class="font-weight-bold"
                  >
                    {{ item.TerminalNickname }}
                  </div>

                  <div
                    v-if="!!getTerminal(item.TerminalVendorId, item.TerminalModelId)"
                    class="grey--text"
                  >
                    {{ getTerminal(item.TerminalVendorId, item.TerminalModelId).Manufacturer }} - {{ getTerminal(item.TerminalVendorId, item.TerminalModelId).Model }}
                  </div>


                  <div
                    v-if="!!getVendorAcquirerTextById(item.TerminalVendorId, item.TerminalAcquirerCode)"
                    class="grey--text"
                  >
                    {{ getVendorAcquirerTextById(item.TerminalVendorId, item.TerminalAcquirerCode) }}
                  </div>
                </div>
              </template>

              <template #[`item.order_id`]="{item}">
                <div
                  v-if="item.SOrderCode"
                  style="cursor: pointer;"
                  @click="copyToClipboard(item.SOrderCode)"
                >
                  {{ item.SOrderCode }}
                </div>

                <v-chip
                  v-if="item.Table"
                  :color="`#${item.TableAreaColor}`"
                  :style="`color: ${(invertColor(item.TableAreaColor || 'e0e0e0', true))}`"
                  class="px-2 my-1"
                  label
                  x-small
                  @click="onTableChipClick(item)"
                >
                  {{ item.Table }}
                </v-chip>
              </template>

              <template #[`item.amount`]="{item}">
                {{ item.Amount | currency }}
              </template>

              <template #[`item.refund`]="{item}">
                <v-icon
                  v-if="item.TransactionRefundId && !item.TransactionRefundFailedId"
                  color="green"
                >
                  mdi-credit-card-refund
                </v-icon>
                <v-icon
                  v-else-if="item.TransactionRefundId && item.TransactionRefundFailedId"
                  color="red"
                >
                  mdi-credit-card-refund
                </v-icon>
              </template>

              <template #[`item.failed_at`]="{item}">
                <div class="text-no-wrap red--text">
                  <template v-if="item.FailedAt">
                    {{ item.FailedAt }}
                  </template>
                  <template v-else-if="item.TransactionRefundId && item.TransactionRefundFailedId">
                    {{ item.UpdatedAt }}
                  </template>
                </div>
              </template>

              <template #[`item.payment_type`]="{item}">
                <template v-if="item.CardType && CardNameEnum.has(item.CardType)">
                  <card-brand-svg-icon
                    :name="item.CardType"
                    width="26"
                  />
                </template>

                <template v-else>
                  <template v-for="(payment, index) in paymentTypes">
                    <div
                      v-if="item.PaymentType === payment.code"
                      :key="`payment-type-${index}`"
                    >
                      <v-tooltip
                        :color="payment.color2"
                        right
                      >
                        <template #activator="{on}">
                          <img
                            v-if="!!payment.image"
                            :class="'mx-1'"
                            :height="20"
                            :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${payment.image}`"
                            style="margin-top: 2px; margin-bottom: 2px;"
                            width="auto"
                            v-on="on"
                          >
                          <v-icon
                            v-else
                            :class="'mx-1'"
                            :color="payment.color1"
                            :size="22"
                            style="margin-top: 2px; margin-bottom: 2px;"
                            v-on="on"
                          >
                            {{ payment.code === PaymentTypeEnum.CASH ? appCurrency.Icon : payment.icon }}
                          </v-icon>
                        </template>

                        <span>
                          {{
                            payment.code === PaymentTypeEnum.CASH ? $t('Order.PaymentMethod.Cash').Title.toLocaleUpperCase($i18n.locale) : payment.code === PaymentTypeEnum.CARD ? $t('Order.PaymentMethod.Card').Title.toLocaleUpperCase($i18n.locale) : payment.name.toLocaleUpperCase($i18n.locale)
                          }}
                          {{ item.CardType }}
                        </span>
                      </v-tooltip>
                    </div>
                  </template>
                </template>
              </template>

              <template #[`item.transaction_last_type`]="{item}">
                <status-chip
                  v-if="getTransactionType(item.UserConfirmedAt ? 'SALE' : item.TransactionLastType)"
                  :avatar-color="`${getTransactionType(item.UserConfirmedAt ? 'SALE' : item.TransactionLastType).Color} darken-2`"
                  :color="getTransactionType(item.UserConfirmedAt ? 'SALE' : item.TransactionLastType).Color"
                  :icon="getTransactionType(item.UserConfirmedAt ? 'SALE' : item.TransactionLastType).Icon"
                  :label="getTransactionType(item.UserConfirmedAt ? 'SALE' : item.TransactionLastType).Name"
                  :min-width="115"
                  dense
                />
              </template>

              <template #[`item.transaction_last_result`]="{item}">
                <div class="text-no-wrap">
                  <status-chip
                    v-if="getTransactionStatus(item.UserConfirmedAt ? 'COMPLETED' : item.TransactionLastStatus)"
                    :avatar-color="`${getTransactionStatus(item.UserConfirmedAt ? 'COMPLETED' : item.TransactionLastStatus).Color} darken-2`"
                    :color="getTransactionStatus(item.UserConfirmedAt ? 'COMPLETED' : item.TransactionLastStatus).Color"
                    :icon="getTransactionStatus(item.UserConfirmedAt ? 'COMPLETED' : item.TransactionLastStatus).Icon"
                    :label="getTransactionStatus(item.UserConfirmedAt ? 'COMPLETED' : item.TransactionLastStatus).Name"
                    :tooltip="getTransactionStatus(item.UserConfirmedAt ? 'COMPLETED' : item.TransactionLastStatus).Tooltip"
                    dense
                    icon-only
                  />

                  <status-chip
                    v-if="getTransactionResult(item.UserConfirmedAt ? 'APPROVED_MANUAL' : item.TransactionLastResult)"
                    :avatar-color="`${getTransactionResult(item.UserConfirmedAt ? 'APPROVED_MANUAL' : item.TransactionLastResult).ColorAvatar} darken-2`"
                    :color="getTransactionResult(item.UserConfirmedAt ? 'APPROVED_MANUAL' : item.TransactionLastResult).Color"
                    :icon="getTransactionResult(item.UserConfirmedAt ? 'APPROVED_MANUAL' : item.TransactionLastResult).Icon"
                    :label="getTransactionResult(item.UserConfirmedAt ? 'APPROVED_MANUAL' : item.TransactionLastResult).Name"
                    :min-width="115"
                    :tooltip="getTransactionResultTooltip(item.UserConfirmedAt ? 'APPROVED_MANUAL' : item.TransactionLastResult, {user: item.User}, item?.Message || '')"
                    dense
                  />
                </div>
              </template>

              <template #[`item.staff`]="{item}">
                <user-chip
                  v-if="item.Staff"
                  :color="item.StaffColor"
                  :full-name="item.Staff"
                  :short-name="item.StaffShortName"
                />
              </template>

              <template #[`item.updated_at`]="{item}">
                <div class="text-no-wrap">
                  {{ item.UpdatedAt || item.UserConfirmedAt }}
                </div>
              </template>

              <template #[`item.actions`]="{item}">
                <v-speed-dial
                  v-model="item.fab"
                  direction="left"
                  transition="slide-x-reverse-transition"
                >
                  <template #activator>
                    <v-btn
                      :small="$vuetify.breakpoint.xsOnly"
                      class="blue-grey lighten-2 elevation-2 ma-0"
                      dark
                      icon
                    >
                      <v-icon
                        v-if="item.fab"
                        :small="$vuetify.breakpoint.xsOnly"
                      >
                        close
                      </v-icon>
                      <v-icon
                        v-else
                        :small="$vuetify.breakpoint.xsOnly"
                      >
                        menu
                      </v-icon>
                    </v-btn>
                  </template>

                  <v-tooltip
                    :color="$t('Payment.Table.Button.View.Color')"
                    top
                  >
                    <template #activator="{on}">
                      <v-btn
                        :class="$t('Payment.Table.Button.View.Color')"
                        :small="$vuetify.breakpoint.xsOnly"
                        class="ma-0 ml-1"
                        dark
                        icon
                        @click="getItemDetails(item)"
                        v-on="on"
                      >
                        <v-icon :small="$vuetify.breakpoint.xsOnly">
                          {{ $t('Payment.Table.Button.View.Icon') }}
                        </v-icon>
                      </v-btn>
                    </template>

                    <span>{{ $t('Payment.Table.Button.View.Title') }}</span>
                  </v-tooltip>

                  <template v-if="canRefundTransaction(item)">
                    <v-tooltip
                      :color="$t('Payment.Table.Button.Refund.Color')"
                      top
                    >
                      <template #activator="{on}">
                        <v-btn
                          :class="$t('Payment.Table.Button.Refund.Color')"
                          :small="$vuetify.breakpoint.xsOnly"
                          class="ma-0 ml-1"
                          dark
                          icon
                          @click="showConfirmRefundTransactionDialog(item)"
                          v-on="on"
                        >
                          <v-icon :small="$vuetify.breakpoint.xsOnly">
                            {{ $t('Payment.Table.Button.Refund.Icon') }}
                          </v-icon>
                        </v-btn>
                      </template>

                      <span>{{ $t('Payment.Table.Button.Refund.Title') }}</span>
                    </v-tooltip>
                  </template>

                  <template v-if="userIsSuperAdmin">
                    <template v-if="item?.OrderId">
                      <v-tooltip
                        color="deep-orange"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :small="$vuetify.breakpoint.xsOnly"
                            class="ma-0 ml-1 deep-orange"
                            dark
                            icon
                            @click="showConfirmManualAcceptCardTransactionDialog(item)"
                            v-on="on"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              mdi-account-check
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAccept.Title') }}</span>
                      </v-tooltip>
                    </template>

                    <v-tooltip
                      color="indigo"
                      top
                    >
                      <template #activator="{on}">
                        <v-btn
                          :small="$vuetify.breakpoint.xsOnly"
                          class="ma-0 ml-1 indigo"
                          dark
                          icon
                          @click="initCartFromEftPayment(item)"
                          v-on="on"
                        >
                          <v-icon :small="$vuetify.breakpoint.xsOnly">
                            mdi-cart-arrow-down
                          </v-icon>
                        </v-btn>
                      </template>

                      <span>Create Order From EFT/POS Payment</span>
                    </v-tooltip>

                    <template v-if="item?.OrderCode || (!item?.OrderCode && item?.TransactionLastResult === 'APPROVED' && item?.TransactionLastStatus === 'COMPLETED')">
                      <v-tooltip
                        :color="$t('Payment.Table.Button.Refresh.Color')"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :class="$t('Payment.Table.Button.Refresh.Color')"
                            :small="$vuetify.breakpoint.xsOnly"
                            class="ma-0 ml-1"
                            dark
                            icon
                            @click="refreshTransaction(item.Id)"
                            v-on="on"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              {{ $t('Payment.Table.Button.Refresh.Icon') }}
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('Payment.Table.Button.Refresh.Title') }}</span>
                      </v-tooltip>
                    </template>
                  </template>
                </v-speed-dial>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <eft-pos-transaction-details-dialog
      :item="selectedDetailsItem"
      :visible.sync="detailsDialogVisible"
    />

    <payment-terminals-send-all-dialog
      :vendor="terminalResendAllDialogVendor"
      :visible.sync="terminalResendAllDialogVisible"
    />

    <confirm-dialog
      :html-content="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmRefund.Content')"
      :html-title="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmRefund.Title')"
      :info-bar="true"
      :info-bar-body="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmRefund.Notification')"
      :visible.sync="confirmDialogRefundVisible"
      :width="560"
      info-bar-class="purple lighten-4"
      no-button-class="v-btn--outline grey--text"
      title-class="purple white--text lighten-0"
      yes-button-class="purple white--text"
      yes-button-event="on-confirm-refund"
      @on-confirm-refund="onRefundTransaction"
    />

    <confirm-dialog
      :html-content="isManualAcceptedTransaction(selectedTransaction) ? $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Content') : $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Content')"
      :html-title="isManualAcceptedTransaction(selectedTransaction) ? $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Title') : $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Title')"
      :info-bar="isManualAcceptedTransaction(selectedTransaction) ? !!$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Notification') : !!$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Notification')"
      :info-bar-body="isManualAcceptedTransaction(selectedTransaction) ? $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Notification') : $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Notification')"
      :visible.sync="confirmDialogManualAcceptTransVisible"
      :width="580"
      info-bar-class="red lighten-2 white--text"
      no-button-class="v-btn--outline grey--text"
      title-class="orange white--text lighten-0"
      yes-button-class="orange white--text"
      yes-button-event="on-confirm-manual-accept"
      @on-confirm-manual-accept="onManualAcceptCardTransaction"
    />

    <email-select-dialog
      :visible.sync="emailSelectDialogVisible"
      use-user-email
      @save:dialog="onEmailSelected"
    />
  </div>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import DataTable from '@/mixins/data-table/dataTable'
import FilterCommon from '@/mixins/filter/filterCommon'
import FilterSearch from '@/components/common/filter/FilterSearch'
import FilterOptions from '@/components/common/filter/FilterOptions'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'
import StatusChip from '@/components/common/StatusChip.vue'
import EftPosTransactionDetailsDialog from '@/components/payments/EftPosTransactionDetailsDialog.vue'
import TerminalVendorEnum from '@/api/Enums/TerminalVendorEnum'
import PaymentTerminalsSendAllDialog from '@/components/payments/orders/PaymentTerminalsSendAllDialog.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import ToolbarPeriodSelector from '@/components/common/ToolbarPeriodSelector.vue'
import PinManagerEnum from '@/api/Enums/PinManagerEnum.js'
import PaymentType from '@/mixins/orders/paymentType.js'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum.js'
import {getContrastColor} from '@/lib/color/index.js'
import {clone, invertColor} from '@/lib/helper/helper.js'
import EmailSelectDialog from '@/components/common/EmailSelectDialog.vue'
import CardNameEnum from '@/api/Enums/CardNameEnum.js'
import CardBrandSvgIcon from '@/components/common/CardBrandSvgIcon.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  name      : 'EftPosTransactions',
  components: {
    UserChip,
    CardBrandSvgIcon,
    EmailSelectDialog,
    ToolbarPeriodSelector,
    ConfirmDialog,
    PaymentTerminalsSendAllDialog,
    EftPosTransactionDetailsDialog,
    StatusChip,
    FilterSearch,
    FilterOptions
  },
  directives: {},
  mixins    : [AppData, Auth, DataTable, FilterCommon, TerminalsCommon, PaymentType],
  props     : {
    searchParam: {
      type   : String,
      default: ''
    }
  },
  DataStore: {tables: 'tables'},
  data() {
    return {
      selectedItem                         : null,
      selectedDetailsItem                  : null,
      emailSelectDialogVisible             : false,
      detailsDialogVisible                 : false,
      terminalResendAllDialogVisible       : false,
      confirmDialogManualAcceptTransVisible: false,
      selectedTransaction                  : null,
      terminalResendAllDialogVendor        : '',
      confirmDialogRefundVisible           : false,
      filtersModel                         : {
        lang      : 'Payment.Table',
        filterType: {
          key  : 'filter_type',
          value: [],
          items: []
        },
        filterPaymentType: {
          key  : 'filter_payment_type',
          value: [],
          items: []
        },
        filterStatus: {
          key  : 'filter_status',
          value: null,
          items: []
        },
        filterResult: {
          key  : 'filter_result',
          value: [],
          items: []
        },
        filterTerminal: {
          key  : 'filter_terminal',
          value: null,
          items: []
        },
        filterTable: {
          key  : 'filter_table',
          value: null,
          items: []
        }
      },
      dataTable: {
        dataStore    : null,
        socketCommand: {
          list: window.SocketCommand.Payment.EftPos.All
        },
        filter: {
          filter_date: null
        },
        options: {
          defaultSortBy: 'created_at',
          sortBy       : ['created_at'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Payment.Table.Headers.Id'),
            value    : 'id',
            align    : 'right',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.TransactionId'),
            value    : 'transaction_id',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.OrderCode'),
            value    : 'order_code',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.Terminal'),
            value    : 'terminal',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.Order'),
            value    : 'order_id',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.Amount'),
            value    : 'amount',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.PaymentType'),
            value    : 'payment_type',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.TransactionType'),
            value    : 'transaction_last_type',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.TransactionResult'),
            value    : 'transaction_last_result',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.User'),
            value    : 'staff',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.Date'),
            value    : 'updated_at',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Payment.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2',
            width    : '50px'
          }
        ]
      }
    }
  },
  computed: {
    CardNameEnum() {
      return CardNameEnum
    },
    TerminalVendorEnum() {
      return TerminalVendorEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    appHasEpayTerminals() {
      return !!this.appConfig?.POS_VENDORS?.find(vendor => vendor.Code === TerminalVendorEnum.EPAY)?.Terminals?.some(terminal => terminal.Active)
    },
    appHasEdpsTerminals() {
      return !!this.appConfig?.POS_VENDORS?.find(vendor => vendor.Code === TerminalVendorEnum.EDPS)?.Terminals?.some(terminal => terminal.Active)
    },

    filterTypeItems() {
      return Object.values(this.$t('Payment.Transaction.Type')).map(item => {
        return {
          Name  : item.Name,
          Code  : item.Code,
          Avatar: {
            Tile : true,
            Color: item.Color,
            Icon : item.Icon
          }
        }
      })
    },

    filterPaymentTypeItems() {
      const card = this.$t('Order.PaymentMethod.Card')
      const iris = this.$t('Order.PaymentMethod.PosIris')
      return [
        {
          Name  : card.Title,
          Code  : card.Code,
          Avatar: {
            Tile : true,
            Color: card.Color,
            Icon : card.Icon
          }
        },
        {
          Name  : iris.Title,
          Code  : iris.Code,
          Avatar: {
            Tile : true,
            Color: 'transparent',
            Img  : `${this.appConfig.LOCATION_DATA.CdnUrl}assets/payments/${iris.Image}`
          }
        }
      ]
    },

    filterStatusItems() {
      return Object.values(this.$t('Payment.Transaction.Status')).map(item => {
        return {
          Name  : item.Name,
          Code  : item.Code,
          Avatar: {
            Tile : true,
            Color: item.Color,
            Icon : item.Icon
          }
        }
      })
    },

    filterResultItems() {
      return Object.values(this.$t('Payment.Transaction.Result')).map(item => {
        return {
          Name  : item.Desc,
          Code  : item.Code,
          Custom: item.Custom,
          Avatar: {
            Tile : true,
            Color: item.ColorAvatar,
            Icon : item.Icon
          }
        }
      })
    },

    filterTableItems() {
      const tablesFiltered = []
      const tables = this.tables || []

      tables.forEach(area => {
        area.Tables.forEach(table => {
          tablesFiltered.push({
            Name       : table.Name,
            Description: area.Name,
            Code       : table.Id,
            Avatar     : {
              Tile : true,
              Color: `#${area.Color}`,
              Icon : 'tab_unselected'
            }
          })
        })
      })

      return tablesFiltered
    },

    filterTerminalItems() {
      const vendors = this.appConfig?.POS_VENDORS || []
      const terminals = []

      vendors.forEach(vendor => {
        vendor.Terminals.forEach(terminal => {
          terminals.push(terminal)
        })
      })

      return terminals.map(item => {
        const terminal = this.getTerminal(item.PosVendorId, item.ModelId)
        const model = terminal ? ` / (${terminal.Manufacturer} - ${terminal.Model})` : ''
        const name = `${item.Nickname} ${model}`
        const desc = this.getVendorAcquirerTextById(item.PosVendorId, item.AcquirerCode)

        return {
          Name       : name,
          Description: desc,
          Code       : item.TerminalId,
          Avatar     : {
            Tile : true,
            Color: 'blue',
            Icon : 'mdi-credit-card-wireless'
          }
        }
      })
    }
  },
  watch: {},
  created() {
    this.$bus.$on(window.SocketCommand.Ajax.Get, this.onGetAjaxCommand)
    this.$bus.$on(window.SocketCommand.Payment.EftPos.Filters, this.onFiltersDataResponse)
    this.$bus.$on(window.SocketCommand.Payment.EftPos.Get, this.getItemDetailsResponse)
    this.$bus.$on(window.SocketCommand.Payment.EftPos.Export, this.onExportResult)
    this.$bus.$on(window.SocketCommand.Payment.EftPos.ManualAccept, this.onManualAcceptCardTransactionResponse)
    this.$bus.$on(window.SocketCommand.Cart.InitFromEftPayment, this.onInitCartFromEftPayment)

    // this.getFiltersData()

    // Init Custom Filter Values
    this.filtersModel.filterType.items = this.filterTypeItems
    this.filtersModel.filterPaymentType.items = this.filterPaymentTypeItems
    this.filtersModel.filterStatus.items = this.filterStatusItems
    this.filtersModel.filterResult.items = this.filterResultItems
    this.filtersModel.filterTerminal.items = this.filterTerminalItems
    this.filtersModel.filterTable.items = this.filterTableItems
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Ajax.Get, this.onGetAjaxCommand)
    this.$bus.$off(window.SocketCommand.Payment.EftPos.Filters, this.onFiltersDataResponse)
    this.$bus.$off(window.SocketCommand.Payment.EftPos.Get, this.getItemDetailsResponse)
    this.$bus.$off(window.SocketCommand.Payment.EftPos.Export, this.onExportResult)
    this.$bus.$off(window.SocketCommand.Payment.EftPos.ManualAccept, this.onManualAcceptCardTransactionResponse)
    this.$bus.$off(window.SocketCommand.Cart.InitFromEftPayment, this.onInitCartFromEftPayment)
  },
  methods: {
    invertColor,
    getContrastColor,

    initCartFromEftPayment(item) {
      window.callAS(window.SocketCommand.Cart.InitFromEftPayment, {Id: item.Id})
    },
    onInitCartFromEftPayment(data, payload) {
      if (data && data.CartId && parseInt(data.CartId) > 0) {
        this.$router.push({
          name  : 'Pos',
          params: {
            orderId: data.CartId
          }
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Pos.Pos.Notification.Error'),
          timeout: 2000,
          type   : 'error',
          icon   : 'warning'
        })
      }
    },

    isManualAcceptedTransaction(transaction) {
      return !!transaction?.UserConfirmedAt
    },

    showConfirmManualAcceptCardTransactionDialog(transaction) {
      if (!transaction) return

      this.selectedTransaction = transaction

      this.executeWithPinPermissionManager(PinManagerEnum.PosTransactionManualAccept, (success) => {
        if (!success) return

        this.confirmDialogManualAcceptTransVisible = true
      })
    },

    onManualAcceptCardTransaction() {
      if (!this.selectedTransaction || !this.selectedTransaction?.OrderId) return

      const transaction = clone(this.selectedTransaction)
      this.selectedTransaction = null

      const payload = {
        OrderId: transaction?.OrderId || null,
        Revert : this.isManualAcceptedTransaction(transaction),
        Id     : transaction?.Id
      }

      window.callAS(window.SocketCommand.Payment.EftPos.ManualAccept, payload)
    },
    onManualAcceptCardTransactionResponse(data, payload) {
      this.getData()
    },

    onTableChipClick(item) {
      if (!item?.TableId) return

      this.filtersModel.filterTable.value = item.TableId
    },

    canRefundTransaction(item) {
      return PaymentTypeEnum.isCard(item.PaymentType) && ((item.TransactionLastType === this.$t('Payment.Transaction.Type.SALE.Code') && item.TransactionLastResult === this.$t('Payment.Transaction.Result.APPROVED.Code')) || (item.TransactionLastType === this.$t('Payment.Transaction.Type.REFUND.Code') && item.TransactionLastResult !== this.$t('Payment.Transaction.Result.APPROVED.Code')))
    },

    onGetAjaxCommand(payload) {
      const command = window.SocketCommand.Payment.EftPos.All
      const payloadCommandsArray = payload?.command ? Array.isArray(payload?.command) ? payload.command : [payload.command] : []

      if (payloadCommandsArray.length) {
        payloadCommandsArray.forEach(cmd => {
          const payloadCommand = window.SocketCommand.commandToClientFormat(cmd || '')
          if (payloadCommand === command) this.getData()
        })
      }
    },

    getFiltersData() {
      window.callAS(window.SocketCommand.Payment.EftPos.Filters, {})
    },
    onFiltersDataResponse(data) {
    },

    getItemDetails(item) {
      if (!item) return
      this.selectedItem = item
      window.callAS(window.SocketCommand.Payment.EftPos.Get, {Id: item.Id})
    },
    getItemDetailsResponse(data) {
      this.selectedDetailsItem = {
        Item: this.selectedItem,
        Data: data
      }

      this.detailsDialogVisible = true
    },

    showConfirmRefundTransactionDialog(item) {
      this.selectedItem = item
      this.executeWithPinPermissionManager(PinManagerEnum.TransactionRefund, (success) => {
        if (!success) return
        this.confirmDialogRefundVisible = true
      })
    },
    onRefundTransaction() {
      if (!this.selectedItem) return

      const payload = {
        Id: this.selectedItem?.Id || null
      }

      window.callAS(window.SocketCommand.Payment.EftPos.Refund, payload, window.SocketCommand.Payment.EftPos.Refund, this.getWebEcrTimeout(this.selectedItem?.TerminalVendorId))
    },
    onRefundTransactionResponse(data) {
    },

    refreshTransaction(itemId) {
      window.callAS(window.SocketCommand.Payment.EftPos.Refresh, {Id: itemId})
    },
    onRefreshTransactionResponse(data) {
    },

    showTerminalResendAllDialog(vendor) {
      this.terminalResendAllDialogVendor = vendor || ''
      this.terminalResendAllDialogVisible = true
    },

    onExportButtonClick() {
      if (!this.dataTable?.filter?.filter_date) return
      this.emailSelectDialogVisible = true
    },
    onEmailSelected(emails) {
      if (!Array.isArray(emails) || !emails?.length) return

      const dataTablePayload = this.dataTableRequestData()
      const payload = {
        Emails  : emails,
        Search  : this.dataTable.search,
        Period  : this.dataTable.filter.filter_date || null,
        Type    : this.filtersModel?.filterType?.value || null,
        Status  : this.filtersModel?.filterStatus?.value || null,
        Result  : this.filtersModel?.filterResult?.value || null,
        Terminal: this.filtersModel?.filterTerminal?.value || null,
        Table   : this.filtersModel?.filterTable?.value || null,
        SortBy  : dataTablePayload.sort_by,
        OrderBy : dataTablePayload.order_by
      }

      window.callAS(window.SocketCommand.Payment.EftPos.Export, payload)
    },

    onExportResult(response) {
      if (response.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Payment.Table.Toolbar.Button.Export.Success', {email: this.posUser?.staff_email || ''}),
          type   : 'success',
          icon   : 'check',
          timeout: 5000
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
      }
    }
  }
}
</script>

<style scoped>

</style>
